import { useState } from "react";
import { Loader2, Star, AlertOctagon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Box, useAction } from "./JobActions";
import { Field } from "@/components/site/Common";
import { CHECKLIST, INCIDENT_CATS, fdate } from "@/lib/hz";
import { post } from "@/lib/api";

export const QualityForm = ({ jobId }) => {
  const [check, setCheck] = useState({});
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const { busy, run } = useAction();
  const score = CHECKLIST.reduce((a, c) => a + (check[c.id] ? c.weight : 0), 0);
  return (
    <Box title="Control de calidad" testid="quality-form">
      <div className="space-y-2">
        {CHECKLIST.map((c) => (
          <label key={c.id} className="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-200 p-3 text-sm hover:border-orange-300">
            <Checkbox checked={!!check[c.id]} onCheckedChange={(v) => setCheck({ ...check, [c.id]: !!v })} data-testid={`quality-check-${c.id}`} />
            <span className="flex-1">{c.label}</span><span className="text-xs font-semibold text-slate-400">{c.weight} pts</span>
          </label>
        ))}
      </div>
      <div className="mt-3 text-sm font-semibold">Puntuación: <span className="text-orange-600" data-testid="quality-score">{score}/100</span></div>
      <div className="mt-4 flex gap-1">
        {[1, 2, 3, 4, 5].map((i) => (
          <button key={i} type="button" onClick={() => setRating(i)} data-testid={`quality-star-${i}`}><Star className={`size-7 ${i <= rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} /></button>
        ))}
      </div>
      <Textarea className="mt-3" rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Cuéntanos qué tal fue el trabajo" data-testid="quality-comment-input" />
      <Button className="mt-3 w-full rounded-xl" disabled={!!busy || !rating} data-testid="quality-submit-btn"
        onClick={() => run("q", () => post(`/jobs/${jobId}/quality`, { checklist: check, rating, comment }), "¡Gracias! Tu control de calidad se ha registrado")}>
        {busy && <Loader2 className="animate-spin" />}Enviar valoración
      </Button>
    </Box>
  );
};

export const QualityView = ({ q }) => (
  <Box title="Control de calidad" testid="quality-view">
    <div className="flex items-center gap-4"><div className="font-heading text-3xl font-extrabold text-orange-600">{q.score}/100</div>
      <div className="flex">{[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`size-5 ${i <= q.rating ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />)}</div></div>
    {q.comment && <p className="mt-2 text-sm text-slate-600">“{q.comment}”</p>}
    <div className="mt-1 text-xs text-slate-400">{fdate(q.created_at)}</div>
  </Box>
);

export const IncidentDialog = ({ jobId }) => {
  const [open, setOpen] = useState(false);
  const [cat, setCat] = useState(INCIDENT_CATS[0]);
  const [desc, setDesc] = useState("");
  const { busy, run } = useAction();
  const submit = async () => {
    const r = await run("i", () => post(`/jobs/${jobId}/incidents`, { category: cat, description: desc }), "Incidencia registrada. Te contactaremos en menos de 24 h");
    if (r !== undefined) { setDesc(""); setOpen(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" className="w-full rounded-xl border-rose-200 text-rose-700 hover:bg-rose-50" data-testid="open-incident-btn"><AlertOctagon />Abrir incidencia</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-heading">Abrir incidencia</DialogTitle></DialogHeader>
        <Field label="Motivo">
          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger className="h-11" data-testid="incident-category-select"><SelectValue /></SelectTrigger>
            <SelectContent>{INCIDENT_CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Descripción"><Textarea rows={4} value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="incident-description-input" /></Field>
        <Button className="rounded-xl" disabled={!!busy || desc.trim().length < 5} onClick={submit} data-testid="incident-submit-btn">{busy && <Loader2 className="animate-spin" />}Enviar incidencia</Button>
      </DialogContent>
    </Dialog>
  );
};
